/**
 * The 规则 draft editor's state and its conversion to / from the API.
 *
 * Parameters are edited as a list (name, kind, value or program + field) rather than the API's map, so
 * a parameter can be renamed and the order the editor shows is stable. The checks mirror the service's
 * (RuleSectionsValidator) so most mistakes show next to the field instead of after a round trip; the
 * service remains the authority.
 */
import type { RuleData, RuleParamData, RuleSectionData } from '../client/CpaClient';

/** A stable React key for list items that have no id of their own (parameters, table rows). */
let seq = 0;
export const nextKey = () => `k${++seq}`;

export interface EditableParam {
  /** Editor-only; never sent. Keeps a row's inputs attached to it when an earlier row is removed. */
  key: string;
  name: string;
  kind: 'engine' | 'program';
  value: string;
  program: string;
  field: string;
  /** Kept as-is so saving does not drop where the engine implements it. */
  source?: string;
}

export interface EditableRule {
  ruleId: string;
  text: string;
  params: EditableParam[];
  programs: string[];
  source?: string;
}

export interface EditableSection {
  sectionId: string;
  title: string;
  summary: string;
  rules: EditableRule[];
  table?: { columns: { columnId: string; title: string }[]; rows: EditableRow[] };
}

/** A table row: its cells by column id, and an editor-only key. */
export interface EditableRow {
  key: string;
  cells: Record<string, string>;
}

const NAME = /^[a-zA-Z0-9]+$/;
const PLACEHOLDER = /\{([^{}]*)\}/g;

export function toEditable(sections: RuleSectionData[] | undefined): EditableSection[] {
  return (sections ?? []).map((s) => ({
    sectionId: s.sectionId ?? '',
    title: s.title ?? '',
    summary: s.summary ?? '',
    rules: (s.rules ?? []).map((r) => ({
      ruleId: r.ruleId ?? '',
      text: r.text ?? '',
      params: Object.entries(r.params ?? {}).map(([name, p]) => ({
        key: nextKey(),
        name,
        kind: p.kind === 'program' ? 'program' : 'engine',
        value: p.kind === 'program' ? '' : (p.value ?? ''),
        program: p.program ?? '',
        field: p.field ?? 'qualifier_min',
        source: p.source,
      })),
      programs: r.programs ?? [],
      source: r.source,
    })),
    table: s.table
      ? {
          columns: (s.table.columns ?? []).map((c) => ({ columnId: c.columnId ?? '', title: c.title ?? '' })),
          rows: (s.table.rows ?? []).map((row) => ({ key: nextKey(), cells: { ...row } })),
        }
      : undefined,
  }));
}

export function fromEditable(sections: EditableSection[]): RuleSectionData[] {
  return sections.map((s) => ({
    sectionId: s.sectionId,
    title: s.title.trim(),
    summary: s.summary.trim() || undefined,
    rules: s.rules.map((r): RuleData => ({
      ruleId: r.ruleId,
      text: r.text.trim(),
      params: r.params.length
        ? Object.fromEntries(
            r.params.map((p): [string, RuleParamData] => [
              p.name.trim(),
              p.kind === 'program'
                ? {
                    kind: 'program',
                    program: p.program,
                    field: p.field as RuleParamData['field'],
                    source: p.source,
                  }
                : { kind: 'engine', value: p.value.trim(), source: p.source },
            ]),
          )
        : undefined,
      programs: r.programs.length ? r.programs : undefined,
      source: r.source,
    })),
    table: s.table ? { columns: s.table.columns, rows: s.table.rows.map((row) => row.cells) } : undefined,
  }));
}

/** Problems keyed by where they are: `s:<sectionId>` or `r:<ruleId>`. Values are i18n keys. */
export type EditorErrors = Record<string, string[]>;

export function validateEditable(sections: EditableSection[]): EditorErrors {
  const errors: EditorErrors = {};
  const add = (at: string, key: string) => (errors[at] ??= []).push(key);
  for (const s of sections) {
    if (!s.title.trim()) add(`s:${s.sectionId}`, 'editRuleTitleRequired');
    for (const r of s.rules) {
      const at = `r:${r.ruleId}`;
      if (!r.text.trim()) add(at, 'editRuleTextRequired');
      const names = r.params.map((p) => p.name.trim());
      if (names.some((n) => !NAME.test(n))) add(at, 'editRuleParamName');
      if (new Set(names).size !== names.length) add(at, 'editRuleParamDuplicate');
      for (const m of r.text.matchAll(PLACEHOLDER)) {
        if (!NAME.test(m[1]) || !names.includes(m[1])) {
          add(at, 'editRulePlaceholder');
          break;
        }
      }
      if (r.params.some((p) => (p.kind === 'engine' ? !p.value.trim() : !p.program)))
        add(at, 'editRuleParamMissing');
    }
  }
  return errors;
}

/** An id with `prefix` not yet in `taken`: prefix-1, prefix-2, ... */
export function newId(prefix: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  for (let i = 1; ; i++) {
    const id = `${prefix}-${i}`;
    if (!used.has(id)) return id;
  }
}

export const allRuleIds = (sections: EditableSection[]) =>
  sections.flatMap((s) => s.rules.map((r) => r.ruleId));
